import { useCallback, useEffect, useRef, useState } from "react";

import { ContactLeadPanelTab } from "./contactLeadPanel.utils";

import type { LeadContextView } from "./LeadContextTabs";

/** A lead-panel section a host may record: a content tab, or the Chat view beside it. */
export type ContactLeadPanelSection = ContactLeadPanelTab | "chat";

export interface ContactLeadPanelSectionState {
	activeTab: ContactLeadPanelTab;
	/** The non-reporting setter, for resetting the tab on a new prospect. */
	setActiveTab: (section: ContactLeadPanelTab) => void;
	/** Set the tab and tell the host, which may record the section open (PREC-4334). */
	selectSection: (section: ContactLeadPanelTab) => void;
}

/**
 * The lead panel's content tab and, for a host that passes a callback (the Chrome extension),
 * the section opens worth recording: a user-opened tab (not a re-click of the open one, nor the
 * tab the panel resets to when pointed at another lead) and each switch into the Chat view.
 */
export function useContactLeadPanelSection(
	onSectionOpen?: (section: ContactLeadPanelSection) => void,
	contextView?: LeadContextView
): ContactLeadPanelSectionState {
	const [activeTab, setActiveTab] = useState<ContactLeadPanelTab>("overview");
	const lastContextViewRef = useRef(contextView);
	const selectSection = useCallback(
		(section: ContactLeadPanelTab) => {
			if (section === activeTab) {
				return;
			}

			setActiveTab(section);
			onSectionOpen?.(section);
		},
		[activeTab, onSectionOpen]
	);

	useEffect(() => {
		if (contextView === "chat" && lastContextViewRef.current !== "chat") {
			onSectionOpen?.("chat");
		}

		lastContextViewRef.current = contextView;
	}, [contextView, onSectionOpen]);

	return { activeTab, setActiveTab, selectSection };
}
